import type { TranscriptContextMenuActions } from '@src/types.ts/general';

/**
 * Per-surface gates for the original-language context menu. Each host passes
 * these as `hide*` props (reviewer mode, the T&S and Captions tabs, feature
 * flags) or leaves a handler unwired; a gated action is not offered on that
 * surface at all. State-driven unavailability is NOT a gate — it is a disabled
 * row with a reason (`docs/design-system.md` → "Conditional actions").
 */
export interface ContextMenuSurfaceGates {
  hideAddPhoto: boolean;
  hideAddOverlayGraphic: boolean;
  /** Overlay insert is only wired on the clip preview. */
  hasOverlayHandler: boolean;
  hideCutText: boolean;
  hideRemoveText: boolean;
  hideReplace: boolean;
  hideSaveAsClip: boolean;
  /** Highlights need a saved-segment service; hosts without one hide the row. */
  hasSavedSegmentService: boolean;
}

/** Drops the actions this surface never offers. Extracted from `Transcript.tsx`. */
export function applyContextMenuSurfaceGates(
  actions: TranscriptContextMenuActions[],
  gates: ContextMenuSurfaceGates,
): TranscriptContextMenuActions[] {
  const hidden = new Set<TranscriptContextMenuActions>();
  if (gates.hideAddPhoto) hidden.add('addPhoto').add('addPhotoLoading');
  if (gates.hideAddOverlayGraphic || !gates.hasOverlayHandler) {
    hidden.add('addOverlayGraphic').add('addOverlayGraphicLoading');
  }
  if (gates.hideCutText || gates.hideRemoveText) hidden.add('cutText');
  if (gates.hideRemoveText) hidden.add('removeText').add('removeTextLoading');
  if (gates.hideReplace) hidden.add('replace');
  if (gates.hideSaveAsClip) hidden.add('saveAsClip');
  if (!gates.hasSavedSegmentService) hidden.add('saveSegmentForLater');
  return hidden.size ? actions.filter(a => !hidden.has(a)) : actions;
}
